import React from 'react';
import { graphql } from 'gatsby';
import { BaseHead } from '~components/BaseHead';
import { Layout } from '~components/Layout';
import { ContentBlocks } from './components/ContentBlocks';
import { useDeDistributionPartner } from '~hooks/useDeDistributionPartner';
import { useStructuredData } from '~hooks/useStructuredData';

import type { HeadFC, PageProps } from 'gatsby';
import type { Resource } from 'i18next';
import type { RichButtonContent } from '~components/RichButton';
import type { ContentBlockType } from './components/ContentBlocks';
import type { ContentfulComponentSharedMetaFields, UspBarContent } from '~models/contentful';
import type { Country } from '~models/country';
import type { Breadcrumb, JobPosting } from '~build/models';
import type { ContentfulComponentLink } from '~components/ComponentLink';
import type { ContentfulComponentLinkGroup } from '~components/types';

type LandingPageVariant = {
  variant?: string;
  distributionPartner?: 'finom' | 'foma' | 'ionosDe' | 'uplink';
  uspBarContent?: UspBarContent;
  headerCtaItems?: ContentfulComponentLink | ContentfulComponentLinkGroup;
  callToActionText?: string;
  callToActionTarget?: string;
  callToActionDropdown?: RichButtonContent[];
  disableHeaderCta?: boolean;
  contentBlocks: ContentBlockType[];
};

type LandingPageContainer = {
  id: string;
  createdAt: string;
  updatedAt: string;
  metaFields: ContentfulComponentSharedMetaFields;
  variants: LandingPageVariant[];
};

export type LandingDataType = {
  contentfulPageContainer: LandingPageContainer;
};

export type LandingPageContextType = {
  id: string;
  language: Country;
  i18nResources: Resource;
  breadcrumbs: Breadcrumb[];
  variant: string;
  jobPostings: JobPosting[];
};

const getVariant = (variants: LandingPageVariant[], variant: string): LandingPageVariant => {
  if (variants.length === 1) return variants[0];
  return variants.find((landingPageVariant) => variant === landingPageVariant.variant) || variants[0];
};

const Landing: React.FC<PageProps<LandingDataType, LandingPageContextType>> = ({
  data: { contentfulPageContainer },
  pageContext: { variant },
}): JSX.Element => {
  const page = getVariant(contentfulPageContainer.variants, variant);
  useDeDistributionPartner(page.distributionPartner);

  return (
    <Layout headerCta={{ cta: page.headerCtaItems, disable: page.disableHeaderCta }} uspBarContent={page.uspBarContent}>
      <ContentBlocks contentBlocks={page.contentBlocks || []} />
    </Layout>
  );
};

export default Landing;

export const Head: HeadFC<LandingDataType, LandingPageContextType> = ({
  data: { contentfulPageContainer },
  pageContext,
  location,
}): JSX.Element => {
  const { getHomePageStructuredData, getLandingPageStructuredData } = useStructuredData();

  const { metaTitle, metaDescription, metaRobots, metaImage, openGraphTitle, openGraphDescription } =
    contentfulPageContainer.metaFields;

  const metaFields = [
    { name: 'title', content: metaTitle },
    { name: 'description', content: metaDescription },
    { name: 'robots', content: metaRobots },
    { property: 'og:title', content: openGraphTitle || metaTitle },
    { property: 'og:description', content: openGraphDescription || metaDescription },
  ];

  if (metaImage) {
    metaFields.push(
      { property: 'og:image', content: metaImage.file.url },
      { property: 'og:image:type', content: metaImage.mimeType },
      { property: 'og:image:width', content: metaImage.width.toString() },
      { property: 'og:image:height', content: metaImage.height.toString() },
    );
  }

  const structuredData =
    location.pathname === '/'
      ? getHomePageStructuredData(pageContext.breadcrumbs)
      : getLandingPageStructuredData({
          ...contentfulPageContainer,
          path: location.pathname,
          breadcrumbs: pageContext.breadcrumbs,
        });

  return <BaseHead metaFields={metaFields} structuredData={structuredData} />;
};

export const query = graphql`
  query ($id: String) {
    contentfulPageContainer(id: { eq: $id }) {
      id
      createdAt
      updatedAt
      metaFields {
        pageTitle
        metaRobots
        metaTitle
        metaDescription
        openGraphTitle
        openGraphDescription
        metaImage {
          file {
            url
          }
          mimeType
          width
          height
        }
      }
      variants {
        variant
        distributionPartner
        uspBarContent {
          raw
        }
        headerCtaItems {
          ... on Node {
            ... on ContentfulComponentLink {
              ...ComponentLink
            }
            ... on ContentfulComponentLinkGroup {
              ...ComponentLinkGroup
            }
          }
        }
        disableHeaderCta
        contentBlocks {
          ... on Node {
            ... on ContentfulComponentHero {
              ...ComponentHero
            }
            ... on ContentfulComponentTextMedia {
              ...ComponentTextMedia
            }
            ... on ContentfulComponentTextWithToc {
              ...ComponentTextWithToc
            }
            ... on ContentfulComponentUspCarousel {
              ...ComponentUspCarousel
            }
            ... on ContentfulComponentReviews {
              ...ComponentReviews
            }
            ... on ContentfulComponentActivityCards {
              ...ComponentActivityCards
            }
            ... on ContentfulComponentCoverageList {
              ...ComponentCoverageList
            }
            ... on ContentfulComponentCoverageLandingHeader {
              ...ComponentCoverageLandingHeader
            }
            ... on ContentfulComponentCoverageLandingBody {
              ...ComponentCoverageLandingBody
            }
            ... on ContentfulComponentSharedCollapsibleList {
              ...ComponentSharedCollapsibleList
            }
            ... on ContentfulComponentSharedImageText {
              ...ComponentSharedImageText
            }
            ... on ContentfulComponentSharedVideo {
              ...Video
            }
            ... on ContentfulComponentSharedText {
              ...Text
            }
            ... on ContentfulComponentImageTextGroup {
              ...ComponentImageTextGroup
            }
            ... on ContentfulComponentLogoBar {
              ...ComponentLogoBar
            }
            ... on ContentfulComponentHeaderToptasks {
              ...ComponentHeaderToptasks
            }
            ... on ContentfulComponentProductCards {
              ...ComponentProductCards
            }
            ... on ContentfulComponentRelatedArticles {
              ...ComponentRelatedArticles
            }
            ... on ContentfulComponentTable {
              ...ComponentTable
            }
            ... on ContentfulComponentCardGroup {
              ...ComponentCardGroup
            }
            ... on ContentfulComponentCustom {
              ...ComponentCustom
            }
            ... on ContentfulComponentProductTiles {
              ...ComponentProductTiles
            }
          }
        }
      }
    }
  }
`;
